/**
 * Site alert banner — lives inside the fixed header.
 *
 * Keeps --header-height in sync with the alert's rendered height so the
 * fixed header never covers page content. Handles three cases that all
 * used to break the previous one-shot measurement:
 *   1. The alert wraps differently at different viewport widths.
 *   2. Crossing a breakpoint that changes the base header height
 *      (e.g. the `.site-header-aux` rule in _root.scss at $bp-mobile-nav).
 *   3. The alert gets dismissed, so the offset goes back to 0.
 *
 * Dismissal sets a 30-day cookie keyed on the alert's ID.
 */

const alert = document.getElementById("site-alert");
if (alert) {
  const alertId = alert.dataset.alertId;
  const cookieName = `site_alert_dismissed_${alertId}`;
  const root = document.documentElement;

  // Read the CSS-defined header height (without our inline override). We
  // must clear the inline style first, or getComputedStyle returns the
  // override we set last time.
  const measureBase = () => {
    const prev = root.style.getPropertyValue("--header-height");
    if (prev) root.style.removeProperty("--header-height");
    const base = parseInt(
      getComputedStyle(root).getPropertyValue("--header-height"),
      10,
    ) || 0;
    return base;
  };

  const applyHeight = () => {
    const base = measureBase();
    const alertH = alert.hidden ? 0 : alert.offsetHeight;
    root.style.setProperty("--header-height", base + alertH + "px");
  };

  if (!getCookie(cookieName)) {
    // Reveal the alert and expand --header-height to match.
    alert.removeAttribute("hidden");
    applyHeight();

    // Keep --header-height in sync when the alert's height changes
    // (text reflow on viewport resize, dynamic content, etc.) or when
    // the viewport crosses a breakpoint that changes the base header.
    if ("ResizeObserver" in window) {
      new ResizeObserver(applyHeight).observe(alert);
    }
    window.addEventListener("resize", applyHeight, { passive: true });

    // Dismiss button — instant hide, no animation (the slide-up caused
    // a brief flash between the red band collapsing and the rest of the
    // header settling).
    const dismiss = alert.querySelector(".site-alert-dismiss");
    if (dismiss) {
      dismiss.addEventListener("click", () => {
        setCookie(cookieName, "1", 30);

        alert.setAttribute("hidden", "");
        // Clear our override so the CSS-defined header height takes
        // back over cleanly.
        root.style.removeProperty("--header-height");
      });
    }
  }
}

function setCookie(name, value, days) {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${d.toUTCString()};path=/;SameSite=Lax`;
}

function getCookie(name) {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? match[2] : null;
}
